class LoadMore extends HTMLElement {
  connectedCallback() {
    const options = JSON.parse(this.dataset.options || '{}');
    this.sectionId = options.sectionId;
    this.infiniteScroll = options.infiniteScroll;
    this.loading = false;
    this.loadedPages = new Set([this.pageFromUrl(location.href)]);

    this.itemsWrapper = this.querySelector('[data-products-wrapper], [data-articles-wrapper]');

    this.handleClick = this.onClick.bind(this);
    this.bindTriggers();

    if (this.infiniteScroll) this.setupObserver();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.querySelectorAll('[data-load-trigger]').forEach(t => t.removeEventListener('click', this.handleClick));
  }

  pageFromUrl(url) {
    return parseInt(new URL(url, location.origin).searchParams.get('page') || '1');
  }

  bindTriggers() {
    this.querySelectorAll('[data-load-trigger]').forEach(t => t.addEventListener('click', this.handleClick));
  }

  onClick(event) {
    event.preventDefault();
    const link = event.target.closest('[data-load-trigger]');
    if (link) this.load(link.href, link.dataset.loadTrigger);
  }

  async load(url, direction) {
    if (this.loading) return;

    const page = this.pageFromUrl(url);
    if (this.loadedPages.has(page)) {
      direction === 'next'
        ? this.querySelector('[data-pagination-next]')?.closest('.load-more-wrapper')?.classList.add('hidden')
        : this.querySelector('[data-pagination-prev]')?.classList.add('hidden');
      return;
    }

    this.loading = true;

    const trigger = this.querySelector(`[data-load-trigger="${direction}"]`);
    trigger?.querySelector('[data-load-text]')?.classList.add('hidden');
    trigger?.querySelector('[data-loader]')?.classList.remove('hidden');

    try {
      const response = await fetch(`${url}&section_id=${this.sectionId}`);
      const html = document.createRange().createContextualFragment(await response.text());

      const newItems = [...html.querySelectorAll('[data-products-wrapper] li, [data-articles-wrapper] li')];

      if (direction === 'next') {
        newItems.forEach(item => this.itemsWrapper.append(item));
      }
      else {
        newItems.reverse().forEach(item => this.itemsWrapper.prepend(item));
      }

      this.loadedPages.add(page);
      this.updatePagination(html);

      const currentCount = this.itemsWrapper.querySelectorAll('li').length;
      const progressEl = document.querySelectorAll('[data-progress-current]');
      progressEl.forEach(el => el.textContent = currentCount);

      history.pushState(null, '', url);

      window.initDialogs?.(this.itemsWrapper);
      window.dispatchEvent(
        new CustomEvent('window:load-more', {
          detail: { container: this.itemsWrapper },
          bubbles: true,
        }),
      );
    }
    finally {
      trigger?.querySelector('[data-load-text]')?.classList.remove('hidden');
      trigger?.querySelector('[data-loader]')?.classList.add('hidden');
      this.loading = false;
    }
  }

  updatePagination(html) {
    const newNext = html.querySelector('[data-load-trigger="next"]');
    if (newNext && !this.loadedPages.has(this.pageFromUrl(newNext.href))) {
      this.querySelector('[data-load-trigger="next"]').href = newNext.href;
    }
    else {
      this.querySelector('[data-pagination-next]')?.closest('.load-more-wrapper')?.classList.add('hidden');
    }

    const newPrev = html.querySelector('[data-load-trigger="prev"]');
    const prevWrapper = this.querySelector('[data-pagination-prev]');
    if (newPrev && prevWrapper && !this.loadedPages.has(this.pageFromUrl(newPrev.href))) {
      prevWrapper.querySelector('[data-load-trigger="prev"]').href = newPrev.href;
      prevWrapper.classList.remove('hidden');
    }
    else {
      prevWrapper?.classList.add('hidden');
    }
  }

  setupObserver() {
    const sentinel = this.querySelector('[data-pagination-next]');
    if (!sentinel) return;

    this.observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      const nextLink = this.querySelector('[data-load-trigger="next"]');
      if (nextLink) this.load(nextLink.href, 'next');
    },
    { rootMargin: '200px' },
    );

    this.observer.observe(sentinel);
  }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore);
}
